/**
 * Configuration centralisée des types de parcours d'évaluation
 *
 * Ce fichier définit les types de parcours disponibles avec leurs
 * configurations d'évaluations par défaut.
 */

import {
  TemplateEntStructEnum,
  TemplateFormEnum,
  TemplateInbasketEnum,
  TypeEvalEnum,
} from "../../../../convex/schema";

// ============================================
// TYPES
// ============================================

export type TypeParcoursKey =
  | "evaluation_manageriale"
  | "evaluation_competences"
  | "recrutement"
  | "recrutement_kfc"
  | "copsoq3"
  | "recrutement_chef_chantier_ferroviaire"
  | "recrutement_cadre_de_sante";

/**
 * Configuration d'une évaluation au sein d'un parcours
 */
export interface EvalConfig {
  typeEval: TypeEvalEnum;
  template?: TemplateFormEnum | TemplateInbasketEnum | TemplateEntStructEnum;
}

/**
 * Configuration complète d'un type de parcours
 */
export interface ParcoursTypeConfig {
  key: TypeParcoursKey;
  label: string;
  description: string;
  evaluations: EvalConfig[];
}

// ============================================
// CONFIGURATION DES PARCOURS
// ============================================

export const PARCOURS_TYPES: Record<TypeParcoursKey, ParcoursTypeConfig> = {
  evaluation_manageriale: {
    key: "evaluation_manageriale",
    label: "Évaluation managériale",
    description: "Inbasket → Autodiag → Egogramme (3 évaluations)",
    evaluations: [
      {
        typeEval: TypeEvalEnum.INBASKET,
      },
      { typeEval: TypeEvalEnum.FORM, template: TemplateFormEnum.AUTODIAG },
      { typeEval: TypeEvalEnum.FORM, template: TemplateFormEnum.EGOGRAMME },
    ],
  },
  evaluation_competences: {
    key: "evaluation_competences",
    label: "Évaluation compétences Master MOSS",
    description: "Inbasket → Entretien structuré (2 évaluations)",
    evaluations: [
      {
        typeEval: TypeEvalEnum.INBASKET,
        template: TemplateInbasketEnum.DEFAULT,
      },
      {
        typeEval: TypeEvalEnum.FORM,
        template: TemplateFormEnum.AUTODIAG,
      },
    ],
  },
  recrutement: {
    key: "recrutement",
    label: "Recrutement",
    description: "Entretien structuré + PAMI (2 évaluations)",
    evaluations: [
      {
        typeEval: TypeEvalEnum.ENT_STRUCT,
      },
      {
        typeEval: TypeEvalEnum.FORM,
        template: TemplateFormEnum.PAMI,
      },
    ],
  },
  recrutement_kfc: {
    key: "recrutement_kfc",
    label: "Recrutement KFC",
    description: "Entretien structuré + PAMI (2 évaluations)",
    evaluations: [
      {
        typeEval: TypeEvalEnum.ENT_STRUCT,
        template: TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V3,
      },
      {
        typeEval: TypeEvalEnum.FORM,
        template: TemplateFormEnum.PAMI,
      },
    ],
  },
  copsoq3: {
    key: "copsoq3",
    label: "COPSOQ 3",
    description: "COPSOQ 3 (1 évaluation)",
    evaluations: [
      {
        typeEval: TypeEvalEnum.FORM,
        template: TemplateFormEnum.COPSOQ3,
      },
    ],
  },
  recrutement_chef_chantier_ferroviaire: {
    key: "recrutement_chef_chantier_ferroviaire",
    label: "Recrutement chef de chantier ferroviaire",
    description: "Entretien structuré + PAMI (2 évaluations)",
    evaluations: [
      {
        typeEval: TypeEvalEnum.ENT_STRUCT,
        template: TemplateEntStructEnum.CHEF_CHANTIER_FERROVIAIRE_VF_LIGHT,
      },
      {
        typeEval: TypeEvalEnum.FORM,
        template: TemplateFormEnum.PAMI,
      },
    ],
  },
  recrutement_cadre_de_sante: {
    key: "recrutement_cadre_de_sante",
    label: "Recrutement cadre de santé",
    description: "Entretien structuré + PAMI (2 évaluations)",
    evaluations: [
      {
        typeEval: TypeEvalEnum.ENT_STRUCT,
        template: TemplateEntStructEnum.CADRE_SANTE_V_COURTE,
      },
      {
        typeEval: TypeEvalEnum.FORM,
        template: TemplateFormEnum.PAMI,
      },
    ],
  },
};

// ============================================
// HELPERS
// ============================================

export const PARCOURS_OPTIONS = Object.values(PARCOURS_TYPES).map((config) => ({
  value: config.key,
  label: config.label,
  description: config.description,
}));

/** Vrai si BUN_PUBLIC_ALLOWED_PARCOURS_TYPES est définie et non vide. */
export const PARCOURS_TYPES_RESTRICTED: boolean = (() => {
  const raw = process.env.BUN_PUBLIC_ALLOWED_PARCOURS_TYPES;
  if (!raw) return false;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length > 0;
  } catch {
    return false;
  }
})();

/**
 * Retourne les types de parcours autorisés selon BUN_PUBLIC_ALLOWED_PARCOURS_TYPES.
 * Si absente ou vide → tous les types.
 */
export function getFilteredParcoursOptions(): typeof PARCOURS_OPTIONS {
  const raw = process.env.BUN_PUBLIC_ALLOWED_PARCOURS_TYPES;
  if (!raw) return PARCOURS_OPTIONS;
  try {
    const allowed: string[] = JSON.parse(raw);
    if (!Array.isArray(allowed) || allowed.length === 0)
      return PARCOURS_OPTIONS;
    const filtered = PARCOURS_OPTIONS.filter((o) => allowed.includes(o.value));
    return filtered.length > 0 ? filtered : PARCOURS_OPTIONS;
  } catch {
    return PARCOURS_OPTIONS;
  }
}

/**
 * Retourne la configuration d'un type de parcours par sa clé
 */
export function getParcoursConfig(key: TypeParcoursKey): ParcoursTypeConfig {
  return PARCOURS_TYPES[key];
}

const TYPE_EVAL_LABELS: Record<TypeEvalEnum, string> = {
  [TypeEvalEnum.INBASKET]: "Inbasket",
  [TypeEvalEnum.FORM]: "Formulaire",
  [TypeEvalEnum.ENT_STRUCT]: "Entretien structuré",
};

const TEMPLATES_BY_TYPE: Record<TypeEvalEnum, string[]> = {
  [TypeEvalEnum.INBASKET]: Object.values(TemplateInbasketEnum),
  [TypeEvalEnum.FORM]: Object.values(TemplateFormEnum),
  [TypeEvalEnum.ENT_STRUCT]: Object.values(TemplateEntStructEnum),
};

/**
 * Retourne les templates disponibles pour un type d'évaluation.
 * Dérivé directement des enums Convex — toute nouvelle valeur d'enum
 * apparaît automatiquement dans les formulaires.
 */
export function getTemplatesForType(typeEval: TypeEvalEnum): string[] {
  return TEMPLATES_BY_TYPE[typeEval] ?? [];
}

/**
 * Retourne le label d'un type d'évaluation
 */
export function getTypeEvalLabel(typeEval: TypeEvalEnum): string {
  return TYPE_EVAL_LABELS[typeEval];
}

/**
 * Retourne le label d'un template
 */
export function getTemplateLabel(
  _typeEval: TypeEvalEnum,
  template: string,
): string {
  return template;
}
